import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { permitTypes, type PermitType } from '../data/permis'
import type { ShellAtnaTurn, ShellTurn } from '../types/atna'

export type PermisKind = 'projet' | 'details' | 'conformite' | 'documents' | 'frais' | 'succes'

const THINKING_MS = 900

/** Petit classificateur : associe la demande écrite à un type de permis. */
const keywords: Record<string, string[]> = {
  piscine: ['piscine', 'spa', 'baignade', 'bain tourbillon'],
  cloture: ['clôture', 'cloture', 'haie', 'muret', 'palissade'],
  renovation: ['rénov', 'renov', 'salle de bain', 'cuisine', 'sous-sol'],
  agrandissement: ['agrandir', 'agrandissement', 'rallonge', 'garage', 'cabanon', 'étage'],
  arbre: ['arbre', 'abattre', 'abattage', 'frêne', 'érable'],
  stationnement: ['stationnement', 'entrée', 'charretière', 'pavage'],
}

export function detectPermitType(text: string): PermitType | null {
  const value = text.toLowerCase()
  const match = Object.entries(keywords).find(([, list]) => list.some((word) => value.includes(word)))
  if (!match) return null
  return permitTypes.find((type) => type.id === match[0]) ?? null
}

/**
 * Flux ATNA de demande de permis : ATNA identifie le type de permis, précise le
 * projet, valide la conformité au règlement, rassemble les documents, encaisse
 * les frais et dépose la demande.
 */
export function usePermisConversation(initialText: string, onSubmitted: () => void) {
  const seed = initialText.trim()

  const [turns, setTurns] = useState<ShellTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')
  const [permitType, setPermitType] = useState<PermitType | null>(() => detectPermitType(seed))
  const [projectDetail, setProjectDetail] = useState<string | null>(null)
  const [documentsAttached, setDocumentsAttached] = useState(false)

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  const reply = useCallback(
    (kind: PermisKind) => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      setThinking(true)
      timerRef.current = window.setTimeout(() => {
        idRef.current += 1
        setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
        setThinking(false)
        if (kind === 'succes') onSubmitted()
      }, THINKING_MS)
    },
    [onSubmitted],
  )

  const exchange = useCallback(
    (userText: string, kind: PermisKind) => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'user', text: userText }])
      reply(kind)
    },
    [reply],
  )

  useEffect(() => {
    reply('projet')
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is ShellAtnaTurn => turn.role === 'atna'),
    [turns],
  )

  const pickType = useCallback(
    (type: PermitType) => {
      setPermitType(type)
      setProjectDetail(null)
      exchange(type.name, 'details')
    },
    [exchange],
  )

  const pickDetail = useCallback(
    (detail: string) => {
      setProjectDetail(detail)
      exchange(detail, 'conformite')
    },
    [exchange],
  )

  const confirmRules = useCallback(() => {
    exchange('Mon projet respecte ces règles', 'documents')
  }, [exchange])

  const attachDocuments = useCallback(() => {
    setDocumentsAttached(true)
    exchange('Documents joints', 'frais')
  }, [exchange])

  const payFee = useCallback(() => {
    exchange('Payer les frais et déposer ma demande', 'succes')
  }, [exchange])

  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')

    const detected = detectPermitType(text)
    if (lastAtna?.kind === 'projet' && detected) {
      setPermitType(detected)
      exchange(text, 'details')
      return
    }
    if (lastAtna?.kind === 'details') {
      setProjectDetail(text)
      exchange(text, 'conformite')
      return
    }
    exchange(text, lastAtna?.kind ?? 'projet')
  }, [draft, exchange, lastAtna, thinking])

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    permitType,
    projectDetail,
    documentsAttached,
    pickType,
    pickDetail,
    confirmRules,
    attachDocuments,
    payFee,
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind as PermisKind | undefined,
  }
}
